import { FC, ReactNode } from "react";

import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import { translate } from "lib/blocs/TranslateionBloc";
import { APP_BREAKPOINT, APP_CONTENT_WIDTH } from "src/constants/layout";
import { IconCrossXCloseMissed } from "src/constants/icons";
import HtmlParser from "src/lib/HtmlParser";
import { IconStars } from "src/ui/assets/icons/IconStars";
import Translate from "src/ui/components/Translate/Translate";

import AsyncContent from "../AsyncContent/AsyncContent";
import MealInsightProgress from "./MealInsightProgress";
import MealTakenAtButton from "./MealTakenAtButton";
import { formatCalorieRange, formatNutrition } from "./mealInsightHelpers";

const Page = styled.div`
  position: relative;
  margin: 0 auto;
  min-height: 100vh;
  background: var(--color-cream, #fffcf3);
  color: var(--color-charcoal, #212121);

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    max-width: ${APP_CONTENT_WIDTH}px;
    padding-top: 2.5rem;
  }
`;

const ImageSection = styled.div`
  position: relative;
  width: 100%;
`;

const FoodImage = styled(motion.div)<{ imageUrl: string }>`
  width: 100%;
  aspect-ratio: 1;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    aspect-ratio: auto;
    height: 295px;
    border-radius: 0.5rem;
  }
`;

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 26%,
    rgba(0, 0, 0, 0.69) 50%
  );

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    border-radius: 0.5rem;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 200px;
  background: color-mix(
    in srgb,
    var(--color-charcoal, #212121) 12%,
    transparent
  );
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  backdrop-filter: blur(8px);

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: var(--color-white, #fff);
  }

  svg path {
    fill-opacity: 1;
  }

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    position: fixed;
    z-index: 10;

    svg {
      color: var(--color-charcoal, #212121);
    }
  }
`;

const HeaderOverlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 1.5rem;
  color: white;
  z-index: 1;
`;

/* Figma pairs the title and its date button as one tight block — no gap between
   them — while the calories line keeps the overlay's own rhythm. */
const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const MealTitle = styled.h1`
  margin: 0;
  font-family: "STK Bureau Serif", serif;
  font-weight: 300;
  font-size: 2rem;
  line-height: 2.375rem;
  letter-spacing: -0.48px;
`;

const CaloriesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.32px;
  white-space: nowrap;
`;

const CalorieBadge = styled.span`
  padding: 2px 6px 1px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(6px);
  font-size: 0.875rem;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
`;

const InsightsSection = styled.div`
  position: relative;
  z-index: 1;
  margin-top: -1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1.5rem 1rem;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem 0 0.5rem;
  background: var(--color-white, #fff);
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow:
    -5px 5px 16px rgba(69, 49, 22, 0.08),
    -20px 20px 29px rgba(69, 49, 22, 0.07),
    -46px 45px 39px rgba(69, 49, 22, 0.03);
`;

const InsightsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1rem;
`;

const Eyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #5f1f8f;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.375;
  letter-spacing: -0.32px;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const InsightsText = styled.div`
  font-family: "STK Bureau Serif", serif;
  font-size: 1rem;
  line-height: 1.4;
  letter-spacing: -0.16px;
  color: var(--color-charcoal, #212121);

  p {
    margin: 0 0 1rem;
    font-family: "STK Bureau Serif", serif;
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.4;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

const MacroTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem 1rem 0;
  border-top: 1px solid var(--color-light-gray, #e6e3db);
`;

const MacroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
  letter-spacing: -0.28px;
  color: color-mix(in srgb, var(--color-charcoal, #212121) 80%, transparent);
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));

  &[data-sticky="true"] {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 3;
    background: var(--color-cream, #fffcf3);
    border-top: 0.3px solid #e6e3db;
    backdrop-filter: blur(10px);
  }

  @media screen and (min-width: ${APP_BREAKPOINT}px) {
    &[data-sticky="true"] {
      position: static;
      border-top: none;
      backdrop-filter: none;
      border-radius: 0 0 0.5rem 0.5rem;
    }
  }
`;

const Disclaimer = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1rem;
  color: color-mix(in srgb, var(--color-charcoal, #212121) 80%, transparent);
`;

const NoMealBody = styled.div`
  padding: 1.5rem;
`;

const NoMealTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-family: "STK Bureau Serif", serif;
  font-weight: 300;
  font-size: 2rem;
  line-height: 1.2;
`;

export interface MealInsightNutrients {
  protein?: number;
  fiber?: number;
  totalFats?: number;
  totalCarbohydrates?: number;
}

export interface MealInsightContentProps {
  imageUrl?: string;
  imageLoaded: boolean;
  loading: boolean;
  mealIdentified: boolean;
  mealName?: string;
  takenAt?: string;
  onEditTakenAt?: () => void;
  caloriesRange?: { from?: number; to?: number };
  insightsHtml?: string;
  nutrients?: MealInsightNutrients;
  uploadProgress?: {
    phase: "uploading" | "analyzing";
    percentage: number;
    loaded: number;
    total: number;
  };
  showCloseButton?: boolean;
  closeContext?: "home" | "meals";
  onClose?: () => void;
  feedbackSlot?: ReactNode;
  actionSlot?: ReactNode;
  stickyAction?: boolean;
}

const MealImage: FC<{
  imageUrl?: string;
  imageLoaded: boolean;
  alt: string;
}> = ({ imageUrl, imageLoaded, alt }) =>
  imageUrl ? (
    <FoodImage
      imageUrl={imageUrl}
      role="img"
      aria-label={alt}
      initial={{ opacity: 0 }}
      animate={{ opacity: imageLoaded ? 1 : 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    />
  ) : (
    <FoodImage imageUrl="" aria-hidden />
  );

const NoMealInsight: FC<{
  imageUrl?: string;
  imageLoaded: boolean;
  showCloseButton: boolean;
  closeContext: "home" | "meals";
  onClose?: () => void;
}> = ({ imageUrl, imageLoaded, showCloseButton, closeContext, onClose }) => (
  <Page>
    <ImageSection>
      {showCloseButton && (
        <CloseButton
          onClick={onClose}
          aria-label={translate("mealInsights.close", {
            context: closeContext
          })}
        >
          <IconCrossXCloseMissed />
        </CloseButton>
      )}
      <MealImage
        imageUrl={imageUrl}
        imageLoaded={imageLoaded}
        alt={translate("mealInsights.photoUploaded.alt")}
      />
    </ImageSection>
    <NoMealBody>
      <NoMealTitle>
        <Translate msg="mealInsights.notFood.title" />
      </NoMealTitle>
      <InsightsText>
        <p>
          <Translate msg="mealInsights.notFood.description" />
        </p>
      </InsightsText>
    </NoMealBody>
  </Page>
);

const MealInsightContent: FC<MealInsightContentProps> = ({
  imageUrl,
  imageLoaded,
  loading,
  mealIdentified,
  mealName,
  takenAt,
  onEditTakenAt,
  caloriesRange,
  insightsHtml,
  nutrients,
  uploadProgress,
  showCloseButton = false,
  closeContext = "home",
  onClose,
  feedbackSlot,
  actionSlot,
  stickyAction = true
}) => {
  const isLoading = loading;
  const caloryRange = formatCalorieRange(
    caloriesRange?.from,
    caloriesRange?.to
  );

  if (!mealIdentified && !isLoading) {
    return (
      <NoMealInsight
        imageUrl={imageUrl}
        imageLoaded={imageLoaded}
        showCloseButton={showCloseButton}
        closeContext={closeContext}
        onClose={onClose}
      />
    );
  }

  return (
    <Page>
      <ImageSection>
        {showCloseButton && (
          <CloseButton
            onClick={onClose}
            aria-label={translate("mealInsights.close", {
              context: closeContext
            })}
          >
            <IconCrossXCloseMissed />
          </CloseButton>
        )}
        <MealImage
          imageUrl={imageUrl}
          imageLoaded={imageLoaded}
          alt={translate("mealInsights.mealUploaded.alt")}
        />
        {!uploadProgress && <GradientOverlay aria-hidden />}

        <AnimatePresence>
          {uploadProgress && (
            <MealInsightProgress
              phase={uploadProgress.phase}
              percentage={uploadProgress.percentage}
              loaded={uploadProgress.loaded}
              total={uploadProgress.total}
            />
          )}
        </AnimatePresence>

        {!uploadProgress && (
          <HeaderOverlay>
            <TitleBlock>
              <AsyncContent check={[!isLoading]} height="2.375rem" width="80%">
                <MealTitle>{mealName}</MealTitle>
              </AsyncContent>
              {takenAt && onEditTakenAt && (
                <MealTakenAtButton takenAt={takenAt} onPress={onEditTakenAt} />
              )}
            </TitleBlock>
            <CaloriesRow>
              <Translate msg="mealInsights.estimatedCalories" />
              {caloryRange && <CalorieBadge>{caloryRange}</CalorieBadge>}
            </CaloriesRow>
          </HeaderOverlay>
        )}
      </ImageSection>

      <InsightsSection>
        <Card>
          <InsightsBlock>
            <Eyebrow>
              <IconStars />
              <Translate msg="mealInsights.insights" />
            </Eyebrow>
            <AsyncContent check={[!isLoading]} height="1rem">
              <InsightsText>
                {insightsHtml ? new HtmlParser(insightsHtml).toJsx() : null}
              </InsightsText>
            </AsyncContent>
            <AsyncContent check={[!isLoading]} height="1rem" />
            <AsyncContent check={[!isLoading]} height="1rem" width="90%" />
          </InsightsBlock>

          <MacroTable>
            <MacroRow>
              <Translate msg="mealInsights.nutrition.protein" />
              <span>{formatNutrition(nutrients?.protein)}</span>
            </MacroRow>
            <MacroRow>
              <Translate msg="mealInsights.nutrition.fiber" />
              <span>{formatNutrition(nutrients?.fiber)}</span>
            </MacroRow>
            <MacroRow>
              <Translate msg="mealInsights.nutrition.totalFats" />
              <span>{formatNutrition(nutrients?.totalFats)}</span>
            </MacroRow>
            <MacroRow>
              <Translate msg="mealInsights.nutrition.totalCarbs" />
              <span>{formatNutrition(nutrients?.totalCarbohydrates)}</span>
            </MacroRow>
          </MacroTable>
        </Card>

        <Footer>
          {!isLoading && feedbackSlot}
          <Disclaimer>
            <Translate msg="mealInsights.feedback.disclaimer" />
          </Disclaimer>
        </Footer>
      </InsightsSection>

      {!isLoading && actionSlot && (
        <ActionBar data-sticky={stickyAction}>{actionSlot}</ActionBar>
      )}
    </Page>
  );
};

export default MealInsightContent;
